// src/components/tickets/detail/RelatedTicketsCard.tsx
//
// Related tickets (Story 4.2 AC 8). Links from yak_ticket_links are visible and
// navigable from BOTH tickets (the pair is queried in either column), each shown
// as question + status badge + created date. A quiet "Link related ticket"
// action (IN this card, not an action bar) opens an overlay that searches the
// org's tickets by question text, excludes the current ticket and already-linked
// ones, and confirms before linking. The link is created via tx_link_tickets
// (the only creation path at pilot). No unlink path exists anywhere — the confirm
// step says so.
//
// Overlay follows the repo idiom (fixed inset-0, role=dialog, Esc closes, focus
// returns to the trigger with a stable fallback — 1.1's review lesson).

import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '../../ui/Badge'
import { ticketsService } from '../../../services/ticketsService'
import { ticketStatusBadge } from '../../../utils/ticketDisplay'
import { formatDateTime } from '../../../utils/dateUtils'
import type { YakRelatedTicket } from '../../../types/yak'

interface RelatedTicketsCardProps {
  ticketId: string
  relatedTickets: YakRelatedTicket[]
  onLink: (relatedTicketId: string) => Promise<void>
}

export function RelatedTicketsCard({
  ticketId,
  relatedTickets,
  onLink,
}: RelatedTicketsCardProps) {
  const [overlayOpen, setOverlayOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false)
    // Restore focus to the trigger (stable fallback — the button always exists).
    triggerRef.current?.focus()
  }, [])

  const excludeIds = [ticketId, ...relatedTickets.map(r => r.id)]

  return (
    <section className="card p-5" aria-labelledby="related-heading">
      <div className="mb-3 flex items-center justify-between">
        <h2
          id="related-heading"
          className="text-sm font-semibold uppercase tracking-wide text-gray-500"
        >
          Related tickets
        </h2>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOverlayOpen(true)}
          className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
        >
          Link related ticket
        </button>
      </div>

      {relatedTickets.length === 0 ? (
        <p className="text-sm text-gray-500">No related tickets.</p>
      ) : (
        <ul className="space-y-2">
          {relatedTickets.map(t => (
            <li key={t.id} className="flex items-center gap-2 text-sm">
              <Badge variant={ticketStatusBadge(t.status).variant}>
                {ticketStatusBadge(t.status).label}
              </Badge>
              <Link
                to={`/tickets/${t.id}`}
                className="min-w-0 flex-1 truncate text-brand-600 hover:text-brand-700 hover:underline"
              >
                {t.question}
              </Link>
              {t.created_at ? (
                <span className="shrink-0 text-xs text-gray-400">
                  {formatDateTime(t.created_at)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {overlayOpen ? (
        <LinkTicketOverlay
          excludeIds={excludeIds}
          onLink={onLink}
          onClose={closeOverlay}
        />
      ) : null}
    </section>
  )
}

interface LinkTicketOverlayProps {
  excludeIds: string[]
  onLink: (relatedTicketId: string) => Promise<void>
  onClose: () => void
}

function LinkTicketOverlay({
  excludeIds,
  onLink,
  onClose,
}: LinkTicketOverlayProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<YakRelatedTicket[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [selected, setSelected] = useState<YakRelatedTicket | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)

  // Debounced question-text search.
  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed) {
      setResults([])
      setSearchError(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        setSearching(true)
        setSearchError(null)
        const rows = await ticketsService.searchTicketsForLink(
          trimmed,
          excludeIds
        )
        if (!cancelled) setResults(rows)
      } catch {
        if (!cancelled) setSearchError('Search failed — try again in a moment.')
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query, excludeIds])

  const confirmLink = async () => {
    if (!selected) return
    try {
      setSubmitting(true)
      setLinkError(null)
      await onLink(selected.id)
      onClose()
    } catch (err) {
      setLinkError(
        err instanceof Error ? err.message : 'Failed to link the ticket.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-ticket-title"
        aria-describedby={linkError ? 'link-ticket-error' : undefined}
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
      >
        <h3
          id="link-ticket-title"
          className="mb-1 text-lg font-semibold text-gray-900"
        >
          Link related ticket
        </h3>

        {selected ? (
          // Confirm step — names the consequence (links can't be removed).
          <>
            <p className="mb-4 text-sm text-gray-600">
              Links this ticket to “{selected.question}”. Links can&apos;t be
              removed.
            </p>
            {linkError ? (
              <div
                id="link-ticket-error"
                role="alert"
                className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {linkError}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setSelected(null)
                  setLinkError(null)
                }}
                disabled={submitting}
                className="btn-outline"
              >
                Back
              </button>
              <button
                type="button"
                onClick={confirmLink}
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? 'Linking…' : 'Link ticket'}
              </button>
            </div>
          </>
        ) : (
          // Search step.
          <>
            <p className="mb-3 text-sm text-gray-600">
              Search the org&apos;s tickets by question text.
            </p>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by question…"
              aria-label="Search tickets by question text"
              className="mb-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />

            {searchError ? (
              <div
                role="alert"
                className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {searchError}
              </div>
            ) : null}

            <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {searching ? (
                <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>
              ) : results.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-500">
                  {query.trim() ? 'No matching tickets.' : 'Type to search.'}
                </p>
              ) : (
                results.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelected(r)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <Badge variant={ticketStatusBadge(r.status).variant}>
                      {ticketStatusBadge(r.status).label}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-gray-900">
                      {r.question}
                    </span>
                  </button>
                ))
              )}
            </div>

            <div className="mt-4 flex justify-end">
              <button type="button" onClick={onClose} className="btn-outline">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
